import React, { useState, useEffect } from 'react';
import {
  subscribeToTestimonials,
  addTestimonial,
  updateTestimonial,
  approveTestimonial,
  deleteTestimonial,
} from '../../services/firebase';
import { uploadTestimonialImage } from '../../services/firestore';
import { toastSuccess, toastError, toastPromise } from '../../components/admin/ToastProvider';
import { useConfirm, ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { TestimonialListSkeleton, StatsCardSkeleton } from '../../components/admin/Skeleton';
import { TestimonialsEmptyState, SearchEmptyState, ErrorState } from '../../components/admin/EmptyState';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Loader2,
  X,
  MessageSquare,
  Upload,
  Link as LinkIcon,
  Check,
  Star,
  User,
} from 'lucide-react';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [form, setForm] = useState({
    name: '',
    role: '',
    image: '',
    rating: 5,
    text: '',
  });
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [imageSourceType, setImageSourceType] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Confirmation dialog hook
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    const unsubscribe = subscribeToTestimonials(
      (data) => {
        setTestimonials(data);
        setLoading(false);
        setError(null);
      },
      true // Include pending testimonials
    );
    return () => unsubscribe();
  }, []);

  const resetForm = () => {
    setForm({ name: '', role: '', image: '', rating: 5, text: '' });
    setEditing(null);
    setImageSourceType('url');
    setFileError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let imageUrl = form.image;

      if (imageSourceType === 'file' && form.image instanceof File) {
        setUploading(true);
        const uploadPromise = new Promise((resolve, reject) => {
          uploadTestimonialImage(form.image).then(resolve).catch(reject);
        });
        imageUrl = await uploadPromise;
        setUploading(false);
      }

      const testimonialData = {
        ...form,
        image: typeof imageUrl === 'object' ? imageUrl.dataUrl : imageUrl,
      };

      await toastPromise(
        editing
          ? updateTestimonial(editing.id, testimonialData)
          : addTestimonial(testimonialData),
        {
          loading: editing ? 'Memperbarui testimoni...' : 'Menambahkan testimoni...',
          success: editing ? 'Testimoni berhasil diperbarui!' : 'Testimoni berhasil ditambahkan!',
          error: 'Gagal menyimpan testimoni',
        }
      );

      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toastError('Gagal menyimpan testimoni');
      setUploading(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveTestimonial(id);
      toastSuccess('Testimoni berhasil disetujui!');
    } catch (error) {
      console.error('Error approving testimonial:', error);
      toastError('Gagal menyetujui testimoni');
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      role: item.role,
      image: item.image,
      rating: item.rating,
      text: item.text,
    });
    setImageSourceType('url');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm(
      'Hapus testimoni ini? Tindakan tidak dapat dibatalkan.',
      {
        title: 'Hapus Testimoni',
        type: 'danger',
        confirmText: 'Ya, Hapus',
        cancelText: 'Batal',
      }
    );

    if (!confirmed) return;

    try {
      await toastPromise(
        deleteTestimonial(id),
        {
          loading: 'Menghapus testimoni...',
          success: 'Testimoni berhasil dihapus!',
          error: 'Gagal menghapus testimoni',
        }
      );
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toastError('Gagal menghapus testimoni');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setFileError('File size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setFileError('Please select an image file');
        return;
      }
      setForm({ ...form, image: file });
      setFileError('');
    }
  };

  const filteredTestimonials = testimonials.filter((item) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return !item.approved;
    if (filter === 'approved') return item.approved;
    return true;
  });

  const pendingCount = testimonials.filter((t) => !t.approved).length;

  return (
    <div className="space-y-6">
      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-2">
              <div className="h-8 w-56 bg-white/5 rounded animate-pulse" />
              <div className="h-4 w-64 bg-white/5 rounded animate-pulse" />
            </div>
            <div className="h-10 w-40 bg-white/5 rounded-lg animate-pulse" />
          </div>

          {/* Stats Skeleton */}
          <StatsCardSkeleton count={3} />

          {/* Filter Tabs Skeleton */}
          <div className="h-12 bg-white/5 border border-[#D4AF7A]/20 rounded-xl animate-pulse" />

          {/* Testimonials List Skeleton */}
          <TestimonialListSkeleton count={5} />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <ErrorState
          title="Gagal Memuat Data"
          message={error}
          onRetry={() => window.location.reload()}
        />
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-[#F7F5F2]">Testimonials Management</h2>
              <p className="text-[#F7F5F2]/70">Kelola testimoni klien</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              <Plus size={20} />
              Tambah Testimoni
            </button>
          </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Total Testimoni</p>
              <p className="text-2xl font-bold text-[#F7F5F2]">{testimonials.length}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-[#D4AF7A]" />
          </div>
        </div>

        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Disetujui</p>
              <p className="text-2xl font-bold text-green-400">
                {testimonials.filter((t) => t.approved).length}
              </p>
            </div>
            <Check className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Menunggu</p>
              <p className="text-2xl font-bold text-yellow-400">{pendingCount}</p>
            </div>
            <Loader2 className="w-8 h-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#F7F5F2]/50" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
            >
              <option value="all">Semua Testimoni</option>
              <option value="pending">Menunggu Persetujuan</option>
              <option value="approved">Disetujui</option>
            </select>
          </div>
        </div>
      </div>

      {/* Testimonials List */}
      {filteredTestimonials.length === 0 ? (
        searchQuery ? (
          <SearchEmptyState
            onClear={() => {
              setSearchQuery('');
              setFilter('all');
            }}
          />
        ) : (
          <TestimonialsEmptyState
            onAdd={() => {
              resetForm();
              setShowModal(true);
            }}
          />
        )
      ) : (
        <div className="space-y-4">
        {filteredTestimonials.map((item) => (
          <div key={item.id} className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-4 sm:p-6">
            <div className="flex gap-3 sm:gap-4">
              {item.image && (
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                {/* Header section with name and mobile actions */}
                <div className="flex items-start justify-between gap-2 sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-[#F7F5F2] text-sm sm:text-base truncate">{item.name}</h3>
                    <p className="text-xs sm:text-sm text-[#F7F5F2]/60 truncate">{item.role}</p>
                    <div className="flex items-center gap-0.5 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={12}
                          className={i < item.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}
                        />
                      ))}
                    </div>
                  </div>
                  {/* Desktop: Show buttons inline */}
                  <div className="hidden sm:flex items-center gap-2">
                    {!item.approved && (
                      <button
                        onClick={() => handleApprove(item.id)}
                        className="flex items-center justify-center gap-1 px-3 py-1.5 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors text-sm whitespace-nowrap"
                      >
                        <Check size={14} />
                        Approve
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(item)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors text-sm whitespace-nowrap"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm whitespace-nowrap"
                    >
                      <Trash2 size={14} />
                      Hapus
                    </button>
                  </div>
                  {/* Mobile: Icon-only buttons */}
                  <div className="flex sm:hidden items-center gap-1.5">
                    {!item.approved && (
                      <button
                        onClick={() => handleApprove(item.id)}
                        className="flex items-center justify-center p-2 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors"
                        title="Approve"
                      >
                        <Check size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(item)}
                      className="flex items-center justify-center p-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="flex items-center justify-center p-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                      title="Hapus"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-[#F7F5F2]/80 line-clamp-3">{item.text}</p>
              </div>
            </div>
          </div>
        ))}
        </div>
        )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#F7F5F2]">
                {editing ? 'Edit Testimoni' : 'Tambah Testimoni Baru'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#F7F5F2]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Sumber Gambar
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setImageSourceType('url')}
                    className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                      imageSourceType === 'url'
                        ? 'bg-[#D4AF7A] text-black'
                        : 'bg-white/5 text-[#F7F5F2]'
                    }`}
                  >
                    <LinkIcon size={16} className="inline mr-2" />
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageSourceType('file')}
                    className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                      imageSourceType === 'file'
                        ? 'bg-[#D4AF7A] text-black'
                        : 'bg-white/5 text-[#F7F5F2]'
                    }`}
                  >
                    <Upload size={16} className="inline mr-2" />
                    Upload
                  </button>
                </div>
              </div>

              {imageSourceType === 'url' ? (
                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    URL Foto (Opsional)
                  </label>
                  <input
                    type="url"
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    placeholder="https://example.com/photo.jpg"
                    className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                    Upload Foto (Opsional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4AF7A] file:text-black file:cursor-pointer"
                  />
                  {fileError && (
                    <p className="text-xs text-red-400 mt-1">{fileError}</p>
                  )}
                  {uploading && (
                    <div className="flex items-center gap-2 mt-2 text-[#D4AF7A]">
                      <Loader2 size={16} className="animate-spin" />
                      <span className="text-sm">Uploading...</span>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Nama *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nama klien"
                  className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Role/Jabatan (Opsional)
                </label>
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="Misal: Bride, 2024"
                  className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Rating *
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setForm({ ...form, rating })}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        form.rating >= rating
                          ? 'bg-[#D4AF7A] text-black'
                          : 'bg-white/5 text-[#F7F5F2]'
                      }`}
                    >
                      {rating} ⭐
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-2">
                  Testimoni *
                </label>
                <textarea
                  required
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="Pengalaman klien..."
                  rows={4}
                  className="w-full px-4 py-2.5 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2.5 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submitting || uploading}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="inline mr-2 animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    'Simpan'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
